import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { products } from '../data/products';
import { ProductCard } from '../components/products/ProductCard';
import { ProductComparison } from '../components/products/ProductComparison';
import { Button } from '../components/ui/Button';
import { useTranslation } from '../lib/i18n';
interface ProductsPageProps {
  onRequestQuote: () => void;
}
export function ProductsPage({
  onRequestQuote
}: ProductsPageProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const scrollRef = useRef<HTMLDivElement>(null);
  const comparisonRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [focusedProductId, setFocusedProductId] = useState<string | undefined>();
  const [focusNonce, setFocusNonce] = useState(0);
  const handleModelSelect = (productId: string) => {
    setFocusedProductId(productId);
    setFocusNonce(n => n + 1);
    comparisonRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  // Deep-link from the home page: /products?model=<id> focuses that model's
  // column in the comparison table once the page has mounted.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const model = searchParams.get('model');
    if (!model) return;
    const exists = products.some(p => p.id === model);
    if (!exists) return;
    const timer = setTimeout(() => handleModelSelect(model), 350);
    // Clean the query param so a refresh/back doesn't re-trigger.
    setSearchParams({}, {
      replace: true
    });
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleScroll = () => {
    if (!scrollRef.current || !scrollRef.current.children[0]) return;
    const scrollLeft = scrollRef.current.scrollLeft;
    const itemWidth = scrollRef.current.children[0].clientWidth;
    // Calculate which item is currently most visible
    const newIndex = Math.round(scrollLeft / itemWidth);
    setActiveIndex(Math.min(newIndex, products.length - 1));
  };
  const scrollTo = (index: number) => {
    if (!scrollRef.current || !scrollRef.current.children[0]) return;
    const itemWidth = scrollRef.current.children[0].clientWidth;
    scrollRef.current.scrollTo({
      left: index * itemWidth,
      behavior: 'smooth'
    });
  };
  const prev = () => {
    if (activeIndex === 0) {
      // Loop to end
      scrollTo(products.length - 1);
    } else {
      scrollTo(activeIndex - 1);
    }
  };
  const next = () => {
    if (!scrollRef.current) return;
    // Check if we are at the end of the scrollable area
    const isAtEnd = Math.ceil(scrollRef.current.scrollLeft + scrollRef.current.clientWidth) >= scrollRef.current.scrollWidth;
    if (isAtEnd || activeIndex === products.length - 1) {
      // Loop to start
      scrollTo(0);
    } else {
      scrollTo(activeIndex + 1);
    }
  };
  return <main className="min-h-screen bg-dark-bg" data-id="element-1010" data-mp="3002xfltgx9pn">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-dark-surface border-b-2 border-dark-border" data-id="element-1011" data-mp="301x887b0zlg0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1012" data-mp="3dysvp4s0xffl">
          <h1 className="text-5xl md:text-6xl font-black text-white" data-id="element-1013" data-mp="391sournco8nf">
            D-TECH{' '}
            <span className="text-brand-yellow" data-id="element-1014" data-mp="2lbq12nqgxoum">
              {en ? 'products' : ru ? 'продукция' : 'proizvodi'}
            </span>
          </h1>
        </div>
      </section>

      {/* Product Carousel */}
      <section className="py-14 md:py-16 border-b-2 border-dark-border" data-id="element-1015" data-mp="2v1hv9gunofyk">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1016" data-mp="2q9ri734c3e3g">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3 text-center" data-id="element-1017" data-mp="22kfcwec4nq6f">
            {en ? 'Choose a ' : ru ? 'Выберите ' : 'Izaberite '}
            <span className="text-brand-yellow" data-id="element-1018" data-mp="1xkknkf8038nh">
              {en ? 'model' : ru ? 'модель' : 'model'}
            </span>
          </h2>
          <p className="text-gray-400 text-center mb-10 max-w-2xl mx-auto" data-id="element-1019" data-mp="2b686yoxe4lb7">
            {en ? 'SCA, SCB, RAPTOR and SCC series of professional transporters for vertical load handling.' : ru ? 'Серия профессиональных транспортеров для вертикальной транспортировки грузов: SCA, SCB, RAPTOR и SCC.' : 'SCA, SCB, RAPTOR, SCC serija profesionalnih transportera za vertikalni transport tereta.'}
          </p>

          {/* Carousel wrapper */}
          <div className="relative group" data-id="element-1020" data-mp="2yzgmkfuacl3f">
            {/* Left arrow */}
            <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface border border-dark-border text-gray-400 hover:text-white hover:border-brand-yellow transition-colors shadow-lg opacity-0 group-hover:opacity-100 md:opacity-100 focus:opacity-100" aria-label="Prethodni" data-id="element-1021" data-mp="33x9v3fj29r6v">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-1022" data-mp="3n1d64cyofhva" />
            </button>

            {/* Sliding track */}
            <div ref={scrollRef} onScroll={handleScroll} className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-1023" data-mp="3izi7jg84ril7">
              {products.map(product => <div key={product.id} className="flex-shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] snap-center sm:snap-start" data-id="element-1024" data-mp="ejfotp1qtni6">
                  <ProductCard product={product} layout="vertical" onRequestQuote={onRequestQuote} onSelect={handleModelSelect} data-id="element-1025" data-mp="5mycwjlk1lld" />
                </div>)}
            </div>

            {/* Right arrow */}
            <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface border border-dark-border text-gray-400 hover:text-white hover:border-brand-yellow transition-colors shadow-lg opacity-0 group-hover:opacity-100 md:opacity-100 focus:opacity-100" aria-label="Sledeći" data-id="element-1026" data-mp="2mt5eznpymj1g">
              <ChevronRightIcon className="w-5 h-5" data-id="element-1027" data-mp="242feu6rrxaca" />
            </button>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-4" data-id="element-1028" data-mp="28uypovz2trde">
            {products.map((_, idx) => <button key={idx} onClick={() => scrollTo(idx)} className={`transition-all rounded-full ${idx === activeIndex ? 'w-6 h-1.5 bg-brand-yellow' : 'w-1.5 h-1.5 bg-gray-700 hover:bg-gray-500'}`} aria-label={`Strana ${idx + 1}`} data-id="element-1029" data-mp="1v3k6op565q6c" data-mpi={idx} />)}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section ref={comparisonRef} className="py-14 md:py-16 border-b-2 border-dark-border bg-dark-surface scroll-mt-24" data-id="element-1030" data-mp="16hcr4hy4obd6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1031" data-mp="1gf0j504cn4rn">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3 text-center" data-id="element-1032" data-mp="1gd7e1p9dwslr">
            {en ? 'Compare ' : ru ? 'Сравнить ' : 'Uporedi '}
            <span className="text-brand-yellow" data-id="element-1033" data-mp="1pf6v2hkefwop">
              {en ? 'all models' : ru ? 'все модели' : 'sve modele'}
            </span>
          </h2>
          <p className="text-gray-400 text-center mb-10 max-w-2xl mx-auto" data-id="element-1034" data-mp="1kesv7msybt1r">
            {en ? 'Complete range of D-TECH configurations.' : ru ? 'Полный ассортимент конфигураций D-TECH.' : 'Kompletna ponuda D-TECH konfiguracija.'}
          </p>
          <ProductComparison focusProductId={focusedProductId} focusNonce={focusNonce} data-id="element-1035" data-mp="2843oykbvwn6e" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-16 bg-dark-surface" data-id="element-1036" data-mp="3ajaswqxegmpl">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1037" data-mp="3fidycchakuy3">
          <h2 className="text-3xl font-black text-white mb-4" data-id="element-1038" data-mp="3ki0cqp8uwh8h">
            {en ? 'Not sure which model?' : ru ? 'Не уверены, какую модель выбрать?' : 'Niste sigurni koji model?'}
          </h2>
          <p className="text-gray-400 mb-8" data-id="element-1039" data-mp="2ruw48aak1kry">
            {en ? 'Contact us, we will help you choose the right solution for your working conditions.' : ru ? 'Свяжитесь с нами, мы поможем вам выбрать правильное решение для ваших условий работы.' : 'Kontaktirajte nas, pomoći ćemo vam da izaberete pravo rešenje za vaše uslove rada.'}
          </p>
          <Button variant="primary" size="lg" onClick={onRequestQuote} data-id="element-1040" data-mp="292bolujv9c63">
            {en ? 'Request a quote' : ru ? 'Запросить цену' : 'Zatražite ponudu'}
          </Button>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-dark-bg border-t border-dark-border" data-id="element-1041" data-mp="20n49ybo2suu0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1042" data-mp="1wjprleco02de">
          <p className="text-xs text-gray-500 max-w-3xl mx-auto" data-id="element-1043" data-mp="1rl4qlcpl9enw">
            {en ? 'Product photos on the website are for informational purposes and may differ from the actual appearance of the product due to technical modifications by the supplier.' : ru ? 'Фотографии продуктов на сайте носят информационный характер и могут отличаться от реального внешнего вида продукта из-за технических модификаций поставщика.' : 'Fotografije proizvoda na sajtu su informativnog karaktera i mogu odstupati od stvarnog izgleda proizvoda usled tehničkih modifikacija dobavljača.'}
          </p>
        </div>
      </section>
    </main>;
}